import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { SmallText, BodyText } from '../brand/BrandTypography';
import { getEmotions } from '../../services/relationshipToolsService';

/**
 * Emotion Reference Guide - 3 Column Grid Layout
 */

const FALLBACK_EMOTIONS = [
    { emotion_id: 'mad', name: 'Mad', variants: 'Frustrated / Angry / Resentful', color: '#C85A54', inner_voice: '"No one ever listens to me."', body_cues: 'Clenched jaw, flushed face, tension.', underlying_need: 'To feel heard and respected', underlying_fear: 'Fear of being powerless' },
    { emotion_id: 'sad', name: 'Sad', variants: 'Lonely / Disappointed / Hurt', color: '#7D8E95', inner_voice: '"I feel really let down."', body_cues: 'Heaviness in chest, fatigue.', underlying_need: 'To be comforted and understood', underlying_fear: 'Fear of being abandoned' },
    { emotion_id: 'glad', name: 'Glad', variants: 'Content / Grateful / Relieved', color: '#1D6B6B', inner_voice: '"Things feel okay right now."', body_cues: 'Relaxed muscles, easy breathing.', underlying_need: 'To savor positive moments', underlying_fear: 'Fear of losing what feels good' },
    { emotion_id: 'happy', name: 'Happy', variants: 'Joyful / Excited / Playful', color: '#D4A574', inner_voice: '"This is wonderful!"', body_cues: 'Light feeling, smiling, energized.', underlying_need: 'To celebrate and connect', underlying_fear: 'Fear that happiness won\'t last' },
    { emotion_id: 'scared', name: 'Scared', variants: 'Anxious / Worried / Insecure', color: '#7D8E95', inner_voice: '"This feels like too much."', body_cues: 'Racing heart, tight chest.', underlying_need: 'To feel safe and supported', underlying_fear: 'Fear of harm or failure' },
    { emotion_id: 'ashamed', name: 'Ashamed', variants: 'Embarrassed / Humiliated', color: '#7D8E95', inner_voice: '"What\'s wrong with me?"', body_cues: 'Wanting to hide, heat in face.', underlying_need: 'To feel acceptable and worthy', underlying_fear: 'Fear of being seen as flawed' },
    { emotion_id: 'numb', name: 'Numb', variants: 'Disconnected / Empty / Flat', color: '#8A8A8A', inner_voice: '"I don\'t feel anything."', body_cues: 'Heaviness, feeling far away.', underlying_need: 'To feel safe enough to feel', underlying_fear: 'Fear of being overwhelmed' },
    { emotion_id: 'guilty', name: 'Guilty', variants: 'Regretful / Sorry', color: '#7A8A7A', inner_voice: '"I shouldn\'t have done that."', body_cues: 'Pit in stomach, tension.', underlying_need: 'To make amends', underlying_fear: 'Fear of being a bad person' },
    { emotion_id: 'loving', name: 'Loving', variants: 'Tender / Affectionate', color: '#C9A0B0', inner_voice: '"I care so much."', body_cues: 'Warmth in chest, softness.', underlying_need: 'To give and receive love', underlying_fear: 'Fear of rejection or loss' },
    { emotion_id: 'overwhelmed', name: 'Overwhelmed', variants: 'Flooded / Overloaded', color: '#A08090', inner_voice: '"It\'s too much."', body_cues: 'Racing thoughts, shallow breathing.', underlying_need: 'Things to feel manageable', underlying_fear: 'Fear of drowning in demands' }
];

const transformEmotion = (dbEmotion) => ({
    id: dbEmotion.emotion_id,
    name: dbEmotion.name,
    variants: dbEmotion.variants,
    color: dbEmotion.color,
    innerVoice: dbEmotion.inner_voice,
    bodyCues: dbEmotion.body_cues,
    underlyingNeed: dbEmotion.underlying_need,
    underlyingFear: dbEmotion.underlying_fear
});

export const useEmotionData = () => {
    const [emotions, setEmotions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await getEmotions();
                setEmotions(data.map(transformEmotion));
            } catch (err) {
                setEmotions(FALLBACK_EMOTIONS.map(transformEmotion));
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    return { emotions, loading, error };
};

export const EMOTION_DATA = FALLBACK_EMOTIONS.map(transformEmotion);

export const EmotionReferenceGuide = ({ 
    selectedEmotions = [], 
    onEmotionSelect, 
    expandedEmotion,
    onExpandEmotion,
    className 
}) => {
    const { emotions, loading } = useEmotionData();

    if (loading) {
        return (
            <div className={cn('flex items-center justify-center py-8', className)}>
                <div className="w-8 h-8 rounded-full border-2 border-[#7D8E95] border-t-transparent animate-spin" />
            </div>
        );
    }

    const expandedEmotionData = emotions.find(e => e.id === expandedEmotion);

    return (
        <div className={cn('relative', className)}>
            {/* 3 Column Grid Layout */}
            <div className="grid grid-cols-3 gap-3 px-4">
                {emotions.map((emotion) => {
                    const isSelected = selectedEmotions.includes(emotion.id);
                    
                    return (
                        <motion.div
                            key={emotion.id}
                            layout
                            className={cn(
                                'rounded-2xl border-2 overflow-hidden transition-all duration-200',
                                isSelected 
                                    ? 'border-[var(--orenda-soft-teal)] bg-[var(--orenda-soft-teal)]/5 shadow-md' 
                                    : 'border-[var(--orenda-border)] bg-[var(--orenda-soft-white)]'
                            )}
                        >
                            {/* Emotion Card */}
                            <button
                                onClick={() => onEmotionSelect(emotion.id)}
                                className="w-full p-3 flex flex-col items-center text-center relative"
                                data-testid={`emotion-${emotion.id}`}
                            >
                                {isSelected && (
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[var(--orenda-soft-teal)] flex items-center justify-center"
                                    >
                                        <span className="text-white text-[10px]">✓</span>
                                    </motion.div>
                                )}
                                <div 
                                    className={cn(
                                        'w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all',
                                        isSelected ? 'ring-2 ring-offset-2' : ''
                                    )}
                                    style={{ 
                                        backgroundColor: `${emotion.color}20`,
                                        ringColor: emotion.color
                                    }}
                                >
                                    <span 
                                        className="text-lg font-semibold"
                                        style={{ color: emotion.color }}
                                    >
                                        {emotion.name.charAt(0)}
                                    </span>
                                </div>
                                
                                <BodyText className="font-semibold text-[var(--orenda-text-primary)] text-sm">
                                    {emotion.name}
                                </BodyText>
                            </button>
                            
                            {/* Learn More Button */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onExpandEmotion(expandedEmotion === emotion.id ? null : emotion.id);
                                }}
                                className="w-full py-2 flex items-center justify-center border-t border-[var(--orenda-border)]/50 hover:bg-[var(--orenda-border)]/20 transition-colors"
                            >
                                <SmallText className="text-[var(--orenda-text-muted)] text-xs">
                                    Learn more
                                </SmallText>
                            </button>
                        </motion.div>
                    );
                })}
            </div>

            {/* Modal Overlay for Details - Using Portal to escape overflow:hidden parents */}
            {typeof document !== 'undefined' && createPortal(
                <AnimatePresence>
                    {expandedEmotion && expandedEmotionData && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm"
                            onClick={() => onExpandEmotion(null)}
                        >
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                                animate={{ scale: 1, opacity: 1, y: 0 }}
                                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                                className="bg-[var(--orenda-soft-white)] rounded-2xl shadow-xl max-w-sm w-full max-h-[85vh] overflow-y-auto"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Header */}
                                <div 
                                    className="p-4 flex items-center justify-between sticky top-0 z-10"
                                    style={{ backgroundColor: `${expandedEmotionData.color}15` }}
                                >
                                    <div className="flex items-center gap-3">
                                        <div 
                                            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: `${expandedEmotionData.color}25` }}
                                        >
                                            <span 
                                                className="text-lg font-semibold"
                                                style={{ color: expandedEmotionData.color }}
                                            >
                                                {expandedEmotionData.name.charAt(0)}
                                            </span>
                                        </div>
                                        <div>
                                            <BodyText className="font-semibold text-[var(--orenda-text-primary)]">
                                                {expandedEmotionData.name}
                                            </BodyText>
                                            <SmallText className="text-[var(--orenda-text-muted)] text-xs">
                                                {expandedEmotionData.variants}
                                            </SmallText>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => onExpandEmotion(null)}
                                        className="w-8 h-8 rounded-full hover:bg-black/5 flex items-center justify-center transition-colors flex-shrink-0"
                                    >
                                        <X className="w-5 h-5 text-[var(--orenda-text-muted)]" />
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="p-4 space-y-3">
                                    <div>
                                        <SmallText className="text-[var(--orenda-text-muted)] font-medium text-[10px] uppercase tracking-wide">
                                            Inner voice
                                        </SmallText>
                                        <SmallText className="text-[var(--orenda-text-secondary)] italic text-sm mt-1 block">
                                            {expandedEmotionData.innerVoice}
                                        </SmallText>
                                    </div>

                                    <div>
                                        <SmallText className="text-[var(--orenda-text-muted)] font-medium text-[10px] uppercase tracking-wide">
                                            Body cues
                                        </SmallText>
                                        <SmallText className="text-[var(--orenda-text-secondary)] text-sm mt-1 block">
                                            {expandedEmotionData.bodyCues}
                                        </SmallText>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-2 pt-2">
                                        <div className="p-3 rounded-xl bg-[var(--orenda-soft-teal)]/10">
                                            <SmallText className="text-[#1D6B6B] font-medium text-[10px] uppercase">
                                                Need
                                            </SmallText>
                                            <SmallText className="text-[var(--orenda-text-primary)] text-xs mt-1 block">
                                                {expandedEmotionData.underlyingNeed}
                                            </SmallText>
                                        </div>
                                        
                                        <div className="p-3 rounded-xl bg-[var(--orenda-blush-rose)]/10">
                                            <SmallText className="text-[#FFBB98] font-medium text-[10px] uppercase">
                                                Fear
                                            </SmallText>
                                            <SmallText className="text-[var(--orenda-text-primary)] text-xs mt-1 block">
                                                {expandedEmotionData.underlyingFear}
                                            </SmallText>
                                        </div>
                                    </div>
                                </div>

                                {/* Select button - sticky at bottom */}
                                <div className="p-4 pt-2 sticky bottom-0 bg-[var(--orenda-soft-white)]">
                                    <button
                                        onClick={() => {
                                            onEmotionSelect(expandedEmotionData.id);
                                            onExpandEmotion(null);
                                        }}
                                        className={cn(
                                            'w-full py-3 rounded-xl font-medium text-sm transition-all',
                                            selectedEmotions.includes(expandedEmotionData.id)
                                                ? 'bg-[var(--orenda-soft-teal)]/20 text-[#1D6B6B]'
                                                : 'bg-[var(--orenda-soft-teal)] text-white'
                                        )}
                                    >
                                        {selectedEmotions.includes(expandedEmotionData.id) ? 'Selected ✓' : 'Select this emotion'}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    );
};

export default EmotionReferenceGuide;
